import { useState, useRef, useCallback, useEffect, type RefObject } from 'react';

import type { ResizeHandleSide } from '../components/ResizeHandle';

export interface UseResizeHandleOptions {
  initialWidth: number;
  initialHeight: number;
  /** Default width to reset to on double-click. Falls back to initialWidth if not provided. */
  defaultWidth?: number;
  /** Default height to reset to on double-click. Falls back to initialHeight if not provided. */
  defaultHeight?: number;
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  /** When true, horizontal resize maintains aspect ratio. Default: true */
  maintainAspectRatio?: boolean;
  disabled?: boolean;
  onResize: (width: number, height: number) => void;
  /** Called on double-click after onResize, for additional cleanup (e.g., clearing stored null values) */
  onReset?: () => void;
}

export interface UseResizeHandleResult {
  width: number;
  height: number;
  aspectRatio: number;
  isDragging: boolean;
  handleResizeStart: (side: ResizeHandleSide) => (e: React.PointerEvent) => void;
}

/** Max time (ms) between clicks to count as double-click */
const DOUBLE_CLICK_THRESHOLD = 300;

/**
 * Hook to manage drag-to-resize functionality.
 * Uses direct DOM manipulation during drag for smooth performance.
 *
 * Double-click detection:
 * - Native `dblclick` events don't fire reliably with `preventDefault()` on `pointerdown`
 * - Instead, we track timestamps: if two `pointerdown` events occur within DOUBLE_CLICK_THRESHOLD, it's a double-click
 * - On double-click, dimensions reset to defaults, `onResize` is called to persist, then `onReset` for cleanup
 * - We also track `hasMovedRef` to avoid persisting on click-without-drag (allows clean double-click UX)
 */
export function useResizeHandle(containerRef: RefObject<HTMLDivElement | null>, options: UseResizeHandleOptions): UseResizeHandleResult {
  const [width, setWidth] = useState(options.initialWidth);
  const [height, setHeight] = useState(options.initialHeight);
  const [aspectRatio, setAspectRatio] = useState(options.initialWidth / options.initialHeight || 16 / 9);
  const [isDragging, setIsDragging] = useState(false);

  // Store options in ref to avoid callback recreation
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Mutable refs for drag state
  const dragStateRef = useRef({ width, height });
  const abortRef = useRef<AbortController | null>(null);
  const lastClickRef = useRef(0); // Timestamp of last pointerdown for double-click detection
  const hasMovedRef = useRef(false); // True if pointer moved during drag (prevents commit on click-without-drag)

  // Sync from props when they change externally (e.g., collab sync)
  useEffect(() => {
    setWidth(options.initialWidth);
    setHeight(options.initialHeight);
    dragStateRef.current = { width: options.initialWidth, height: options.initialHeight };
    if (options.initialWidth && options.initialHeight) {
      setAspectRatio(options.initialWidth / options.initialHeight);
    }
  }, [options.initialWidth, options.initialHeight]);

  // Cleanup on unmount
  useEffect(() => () => abortRef.current?.abort(), []);

  const handleResizeStart = useCallback(
    (side: ResizeHandleSide) => (e: React.PointerEvent) => {
      const opts = optionsRef.current;
      if (opts.disabled) return;

      e.preventDefault();
      e.stopPropagation();

      const container = containerRef.current;
      if (!container) return;

      // Double-click detection: if second click within threshold, reset and bail
      const now = Date.now();
      if (now - lastClickRef.current < DOUBLE_CLICK_THRESHOLD) {
        lastClickRef.current = 0; // Reset to prevent triple-click triggering
        const { maintainAspectRatio = true } = opts;
        const resetW = opts.defaultWidth ?? opts.initialWidth;
        // When maintaining aspect ratio and no default height, calculate from aspect ratio
        // This ensures 640×360 reset (not 640×450) for a 16:9 video resized to 800×450
        const resetH = opts.defaultHeight ?? (maintainAspectRatio ? Math.round(resetW / aspectRatio) : opts.initialHeight);
        setWidth(resetW);
        setHeight(resetH);
        dragStateRef.current = { width: resetW, height: resetH };
        container.style.width = `${resetW}px`;
        // Only set inline height for independent width/height mode; aspect-ratio mode lets content determine height
        container.style.height = maintainAspectRatio ? '' : `${resetH}px`;
        // Always persist reset dimensions; onReset is for additional cleanup (e.g., clearing stored values)
        opts.onResize(resetW, resetH);
        opts.onReset?.();

        return;
      }
      lastClickRef.current = now; // Record timestamp for next click comparison

      setIsDragging(true);
      hasMovedRef.current = false;

      // Capture values at drag start
      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = container.offsetWidth;
      const startHeight = container.offsetHeight;
      const ratio = startWidth / startHeight;
      const { minWidth = 200, maxWidth = 1200, minHeight = 100, maxHeight = 2000, maintainAspectRatio = true } = opts;

      const handleMove = (moveEvent: PointerEvent) => {
        hasMovedRef.current = true;

        if (side === 'bottom') {
          const deltaY = moveEvent.clientY - startY;
          const newHeight = Math.round(Math.max(minHeight, Math.min(maxHeight, startHeight + deltaY)));
          container.style.height = `${newHeight}px`;
          dragStateRef.current = { width: dragStateRef.current.width, height: newHeight };
        } else {
          const deltaX = moveEvent.clientX - startX;
          const direction = side === 'right' ? 1 : -1;
          const newWidth = Math.round(Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX * direction * 2)));
          const newHeight = maintainAspectRatio ? Math.round(newWidth / ratio) : dragStateRef.current.height;
          container.style.width = `${newWidth}px`;
          if (maintainAspectRatio) container.style.height = `${newHeight}px`;
          dragStateRef.current = { width: newWidth, height: newHeight };
        }
      };

      const handleUp = () => {
        setIsDragging(false);
        abortRef.current?.abort();

        // Only persist if pointer actually moved (click-without-drag shouldn't trigger onResize)
        if (hasMovedRef.current) {
          const { width: finalWidth, height: finalHeight } = dragStateRef.current;
          setWidth(finalWidth);
          setHeight(finalHeight);
          optionsRef.current.onResize(finalWidth, finalHeight);
        }

        // Clear inline height for aspect-ratio mode - let content determine height
        if (maintainAspectRatio) {
          container.style.height = '';
        }
      };

      // Abort any existing listeners from interrupted drags (e.g., pointercancel, focus loss)
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      const { signal } = abortRef.current;
      window.addEventListener('pointermove', handleMove, { signal });
      window.addEventListener('pointerup', handleUp, { signal });
      window.addEventListener('pointercancel', handleUp, { signal });
    },
    [containerRef, aspectRatio],
  );

  return { width, height, aspectRatio, isDragging, handleResizeStart };
}
